/**
 * G2/G3 device-health and usage-limit alerts. The detail ({@link DeviceAlertReport}) travels ONLY
 * inside an encrypted `hygiene.report` revision; the hub sees just an opaque ref, a kind code and
 * a level code (see `LoadStatusFrame.alerts`) so it can send an IDs-only push.
 */
import { z } from 'zod';
/** Kind code = index into this list (no names on the wire to the hub). */
/** Append only: the index is the wire code (`permission`: a macOS privacy grant blocks runs). */
/** Append-only: the index is the code in pushes and schedule markers. `wait`: machine-resources. */
export const DEVICE_ALERT_KINDS = ['battery', 'disk', 'usage', 'permission', 'wait'];
export const DeviceAlertKind = z.enum(DEVICE_ALERT_KINDS);
/** Level code = index into this list. */
export const DEVICE_ALERT_LEVELS = ['warn', 'critical'];
export const DeviceAlertLevel = z.enum(DEVICE_ALERT_LEVELS);
export const DeviceAlertRemedyId = z.enum([
    'plug-in',
    'cleanup',
    'switch-account',
    'switch-provider',
    'defer',
    'pace',
    'resume',
    'grant-permission',
]);
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const DeviceAlertRemedy = z.strictObject({
    id: DeviceAlertRemedyId,
    label: z.string().min(1).max(200),
    /** Destructive or account-changing: never applied without the user's explicit consent. */
    needsConsent: z.boolean(),
    /** Optional structured target (account label, worktree count, bytes) for the drill-down. */
    detail: z.string().max(400).optional(),
});
/** `hygiene.report.report` payload discriminated by `kind: 'device-alert'`. */
export const DeviceAlertReport = z.strictObject({
    kind: z.literal('device-alert'),
    alertId: z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/),
    alertKind: DeviceAlertKind,
    level: DeviceAlertLevel,
    title: z.string().min(1).max(200),
    detail: z.string().max(1000),
    /** Numbers shown verbatim in the drill-down (never truncated). */
    metric: z
        .strictObject({
        /** battery: percent; disk: free bytes; usage: usedPct. */
        value: z.number().finite(),
        threshold: z.number().finite(),
        unit: z.enum(['pct', 'bytes']),
        charging: z.boolean().optional(),
        volume: z.string().max(200).optional(),
        window: z.string().max(40).optional(),
        account: z.string().max(160).optional(),
    })
        .optional(),
    forecast: z
        .strictObject({
        /** Estimated exhaustion time at the current burn rate. */
        exhaustsAt: Ts,
        resetsAt: Ts.optional(),
        /** True when exhaustion is expected before the window resets. */
        beforeReset: z.boolean(),
    })
        .optional(),
    /** New runs are held (not started) while this alert is active. */
    parked: z.boolean(),
    remedies: z.array(DeviceAlertRemedy).max(8),
    raisedAt: Ts,
    /** machine-resources: the run a `wait` card opens (a long job it waited on finished). */
    runId: z.string().min(1).max(200).optional(),
    /** Present once the condition cleared; the card disappears. */
    resolvedAt: Ts.optional(),
});
/** Opaque hub-visible alert marker (codes only; `notify` = send an IDs-only push once). */
export const ScheduleAlert = z.strictObject({
    ref: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    kind: z
        .number()
        .int()
        .min(0)
        .max(DEVICE_ALERT_KINDS.length - 1),
    level: z
        .number()
        .int()
        .min(0)
        .max(DEVICE_ALERT_LEVELS.length - 1),
    notify: z.boolean(),
});
//# sourceMappingURL=device-alerts.js.map